import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from '../../components/Card'
import { OperationsTree } from '../../components/OperationsTree'
import { InfoNote } from '../../components/InfoNote'
import { CopyIcon } from '../../components/icons/CopyIcon'
import { WarningIcon } from '../../components/icons/WarningIcon'
import { splitOpsForTree } from './opsTreeData'
import type { MambuConnectionOperation, MambuConnectionOperationToggle } from '../../lib/types'

export interface TenantTabProps {
  remoteEndpoint: string
  operations: MambuConnectionOperation[]
  formOperations: MambuConnectionOperationToggle[]
  onOperationToggle: (operationId: string, isEnabled: boolean) => void
}


export function TenantTab({ remoteEndpoint, operations, formOperations, onOperationToggle }: TenantTabProps) {
  const { t } = useTranslation()
  const { treeOps, deprecatedTreeOps } = useMemo(() => splitOpsForTree(operations), [operations])

  const handleCopy = () => {
    void navigator.clipboard.writeText(remoteEndpoint)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ marginBottom: 16 }}>
        <InfoNote sticky centered>
          <span>{t('mambuMcpDetail.remoteEndpointLabel')}</span>
          <code style={{ fontSize: 12.5, color: 'var(--text-primary)' }}>{remoteEndpoint}</code>
          <button
            type="button"
            className="btn-text mcp-url-copy"
            aria-label={t('mambuMcpDetail.remoteEndpointCopyAria')}
            onClick={handleCopy}
            style={{
              padding: 5,
              borderRadius: 6,
              transition: 'background .15s',
              color: 'var(--accent)',
            }}
          >
            <CopyIcon size={15} />
          </button>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-tertiary)' }}>{t('mambuMcpDetail.remoteEndpointReadOnlyNote')}</span>
        </InfoNote>
      </div>

      {/* `#mcpcfg-tenant-card` is the hook the prototype's viewport-height rule hangs on
          (agent-studio.html:5559 / CSS :1404-1406) — the tree grows with the window here. */}
      <Card id="mcpcfg-tenant-card" title={t('mambuMcpDetail.tenant.cardTitle')} subtitle={t('mambuMcpDetail.tenant.cardSubtitle')}>
        <OperationsTree
          ops={treeOps}
          mode="tenant"
          isChecked={id => formOperations.find(o => o.operationId === id)?.isEnabled ?? false}
          onToggle={onOperationToggle}
          deprecatedOps={deprecatedTreeOps}
          deprecatedGroupLabel={<><WarningIcon />{t('mambuMcpDetail.opsTree.deprecatedGroup')}</>}
          ariaLabel={t('mambuMcpDetail.opsTree.treeAriaLabel', { name: t('mambuMcpDetail.tenant.cardTitle') })}
        />
      </Card>
    </div>
  )
}
